// src/components/settings/ConfirmChangesSheet.jsx
//
// The last step before a save: every pending change, old -> new, then the
// superadmin's own login password. The server checks the password again
// (5 wrong tries = 15-minute lock), so this sheet is the reminder, not the lock.
// Bottom sheet on phones (Capacitor apps), centred dialog on desktop.

import React, { useEffect, useRef, useState } from 'react';
import { matrixDiff } from './RoleMatrix';
import { formatValue } from './settingsShared';

export default function ConfirmChangesSheet({ changes, tabs, onCancel, onConfirm }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pwRef = useRef(null);

  useEffect(() => {
    // Small delay so the sheet is in place before the phone keyboard opens.
    const t = setTimeout(() => pwRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  const tabLabel = Object.fromEntries(tabs.map((t) => [t.id, t.label]));

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Enter your password to confirm.');
      pwRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message || 'Not saved. Try again.');
      setPassword('');
      setBusy(false);
      if (err.status !== 429) pwRef.current?.focus();
    }
  };

  return (
    <div className="bl-drawer-backdrop bl-pay-backdrop st-sheet-backdrop" onClick={() => !busy && onCancel()}>
      <form className="bl-pay st-sheet" role="dialog" aria-modal="true" aria-labelledby="st-confirm-title"
            onClick={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <div className="st-sheet-grip" aria-hidden="true" />
        <header className="bl-drawer-head">
          <div>
            <div className="ft-mono bl-small bl-drawer-eyebrow">Review before saving</div>
            <h2 id="st-confirm-title" className="st-sheet-title">
              {changes.length === 1 ? '1 setting will change' : `${changes.length} settings will change`}
            </h2>
          </div>
          <button type="button" className="bl-icon-btn" onClick={onCancel} disabled={busy} aria-label="Cancel">
            <i className="fas fa-times"></i>
          </button>
        </header>

        <ul className="st-changes">
          {changes.map((c) => (
            <li key={c.setting.key} className="st-change">
              <div className="st-change-head">
                <span className="st-change-label">{c.setting.label}</span>
                <span className="st-change-tab">{tabLabel[c.setting.tab]}</span>
              </div>
              {c.setting.type === 'role_matrix' ? (
                <ul className="st-matrix-diff">
                  {matrixDiff(c.setting, c.setting.value, c.reset ? c.setting.default : c.value).map((d) => (
                    <li key={d.key}>
                      <span className="st-matrix-diff-label">{d.label}</span>
                      <span className="st-change-values">
                        <span className="st-old">{d.from}</span>
                        <i className="fas fa-arrow-right" aria-label="becomes"></i>
                        <span className="st-new">{d.to}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="st-change-values">
                  <span className="st-old">{formatValue(c.setting, c.setting.value)}</span>
                  <i className="fas fa-arrow-right" aria-label="becomes"></i>
                  <span className="st-new">
                    {c.reset ? (c.setting.env_name ? 'Back to .env / default' : `Default (${formatValue(c.setting, c.setting.default)})`) : formatValue(c.setting, c.value)}
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="st-confirm-pw">
          <label htmlFor="st-confirm-password" className="st-field-label">Your login password</label>
          <p className="st-help">Changes apply to every user within about 15 seconds and are recorded in History and the Audit Log.</p>
          <input
            ref={pwRef}
            id="st-confirm-password"
            className="st-input"
            type="password"
            autoComplete="current-password"
            value={password}
            disabled={busy}
            aria-invalid={!!error}
            aria-describedby={error ? 'st-confirm-error' : undefined}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
          />
          {error && <div id="st-confirm-error" className="st-error" role="alert">{error}</div>}
        </div>

        <div className="bl-drawer-actions st-sheet-actions">
          <button type="button" className="bl-btn-secondary" onClick={onCancel} disabled={busy}>Keep editing</button>
          <button type="submit" className="bl-btn-primary" disabled={busy}>
            {busy ? 'Saving…' : changes.length === 1 ? 'Confirm and save' : `Confirm and save ${changes.length}`}
          </button>
        </div>
      </form>
    </div>
  );
}
